import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

// CSV format: isin,value_date,value[,holding_name]
// Header row optional. Date as YYYY-MM-DD.
export default function HoldingDailyValuesUploader() {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState<{ isins: number; rows: number; latest: string | null } | null>(null);
  const { toast } = useToast();

  const loadStats = async () => {
    const { data: distinctIsins } = await supabase
      .from("risk_holding_daily_values" as any)
      .select("isin", { count: "exact", head: false })
      .limit(1000);
    const { count } = await supabase
      .from("risk_holding_daily_values" as any)
      .select("*", { count: "exact", head: true });
    const { data: latest } = await supabase
      .from("risk_holding_daily_values" as any)
      .select("value_date")
      .order("value_date", { ascending: false })
      .limit(1)
      .maybeSingle();
    const uniq = new Set((distinctIsins ?? []).map((r: any) => r.isin));
    setStats({ isins: uniq.size, rows: count ?? 0, latest: (latest as any)?.value_date ?? null });
  };

  useEffect(() => { loadStats(); }, []);

  const upload = async () => {
    setBusy(true);
    try {
      const lines = csv.trim().split(/\r?\n/).filter(l => l.trim().length > 0);
      if (lines.length === 0) throw new Error("No rows.");
      // Skip header if first cell is non-numeric ISIN-looking but second cell isn't a date
      const first = lines[0].split(",").map(s => s.trim());
      const looksLikeHeader = isNaN(Date.parse(first[1])) || /^[a-z]/i.test(first[2] ?? "");
      const dataLines = looksLikeHeader ? lines.slice(1) : lines;

      const rows = dataLines.map(l => {
        const parts = l.split(",").map(s => s.trim());
        const [isin, value_date, valueRaw, holding_name] = parts;
        const value = Number(valueRaw);
        if (!isin || !value_date || !Number.isFinite(value)) throw new Error(`Bad row: ${l}`);
        return { isin: isin.toUpperCase(), value_date, value, holding_name: holding_name || null };
      });

      // Chunked upsert
      const chunkSize = 500;
      let total = 0;
      for (let i = 0; i < rows.length; i += chunkSize) {
        const chunk = rows.slice(i, i + chunkSize);
        const { error } = await supabase
          .from("risk_holding_daily_values" as any)
          .upsert(chunk, { onConflict: "isin,value_date" });
        if (error) throw error;
        total += chunk.length;
      }
      toast({ title: "Upload complete", description: `${total} rows upserted.` });
      setCsv("");
      loadStats();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>§12 Holding Daily Values</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-muted-foreground space-y-1">
          <p>Upload daily total-return index values per holding (ISIN-keyed). Used by the Performance Attribution panel to derive per-sector portfolio returns and produce true Brinson-Fachler decomposition.</p>
          <p><strong>CSV format:</strong> <code>isin,value_date,value[,holding_name]</code> — header row optional, dates as YYYY-MM-DD, values rebased to any base.</p>
        </div>

        {stats && (
          <div className="flex gap-6 text-xs border rounded p-3 bg-muted/30">
            <div><span className="text-muted-foreground">ISINs covered:</span> <span className="font-medium tabular-nums">{stats.isins}</span></div>
            <div><span className="text-muted-foreground">Total rows:</span> <span className="font-medium tabular-nums">{stats.rows.toLocaleString()}</span></div>
            <div><span className="text-muted-foreground">Latest date:</span> <span className="font-medium tabular-nums">{stats.latest ?? "—"}</span></div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs">Paste CSV</Label>
          <Textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={"isin,value_date,value,holding_name\nGB00B7VVMZ49,2025-01-02,103.42,Vanguard FTSE All-World"}
            rows={10}
            className="font-mono text-xs"
          />
        </div>

        <div className="flex gap-2">
          <Button onClick={upload} disabled={busy || !csv.trim()}>{busy ? "Uploading…" : "Upload"}</Button>
          <Button variant="outline" onClick={() => setCsv("")} disabled={busy}>Clear</Button>
        </div>
      </CardContent>
    </Card>
  );
}
